import { Cast } from '@/modules/casts/classes'
import { useCurrentCast } from '@/modules/casts/compositions'
import { useCurrentTeam } from '@/modules/teams/compositions'
import store from '@/store'
import { KeyDict } from '@/types'
import { Scene } from '@/types/scenes'
import { UserProfile } from '@/types/users'
import { computed } from '@vue/composition-api'
import { sortBy } from 'lodash'
import { useAddLog } from '@/modules/logging/compositions'
import { ResultVoid } from '@/modules/errorHandling/results'
import { SceneSourceType } from '../types'
import { removeFileExtension } from '@/modules/kisweui/utils'

const { addLog, addLogError } = useAddLog()

const getCasterNameInScene = (
  currentCast: Cast|null,
  currentTeamMembers: KeyDict<UserProfile>,
  scene: Scene|null,
  sceneId: string
) => {
  const defaultName = currentCast?.getSceneSourceType(sceneId) === SceneSourceType.TalentAnonymous ? 'Guest' : 'Talent'
  const casterId = currentCast?.getCasterIdBySceneId(sceneId)
  if (!casterId || scene === null) return defaultName
  return currentTeamMembers[casterId]?.displayOrFirstLastName ?? defaultName
}

const setSceneInPreview = async (sceneId: string): Promise<ResultVoid<string>> => {
  const logPayload = { sceneId: sceneId }
  const result = await store.dispatch.events.updateCurrentCast((cast) => cast.setPreviewSceneId(sceneId))
  if (result.isFailure) {
    await addLogError('Failed to select scene for preview', result.message, logPayload)
  } else {
    await addLog('Selected scene for preview', 'info', logPayload)
  }
  return result
}

const setSceneInEdit = async (sceneId: string): Promise<void> => {
  const logPayload = { sceneId: sceneId }
  await store.dispatch.events.setEditSceneId(sceneId)
  await addLog('Selected scene to edit', 'info', logPayload)
}

const useCastScenes = () => {
  const { currentCast } = useCurrentCast()
  const { currentTeamMembers } = useCurrentTeam()
  const sortedScenes = computed(() => {
    const scenes = Object.values(currentCast.value?.scenes ?? {})
    return sortBy(scenes, 'order')
  })

  const getSceneNameBySceneId = (sceneId: string) => {
    const scene = currentCast.value?.scenesWithPresets[sceneId] ?? null
    if (scene?.name) return scene.name
    const sceneSourceType = currentCast.value?.getSceneSourceType(sceneId)
    if (sceneSourceType && [SceneSourceType.Talent, SceneSourceType.TalentAnonymous].includes(sceneSourceType)) {
      return getCasterNameInScene(currentCast.value, currentTeamMembers.value, scene, sceneId)
    }
    const activeAssetId = currentCast.value?.getFirstActiveAssetIdBySceneId(sceneId) ?? null
    if (activeAssetId === null) return ''
    return removeFileExtension(scene?.contents[activeAssetId]?.name ?? '')
  }

  const getLastNonPreviewProgramSceneId = (): string|null => {
    const cast = currentCast.value
    if (cast === null) return null
    const lastNonPreviewProgramScene = [...sortedScenes.value].reverse().find((scene: Scene) => {
      if (hasAssignedSource(scene.id) && cast.previewSceneId !== scene.id && cast.programSceneId !== scene.id) {
        return scene
      }
      return null
    })
    return lastNonPreviewProgramScene?.id ?? null
  }

  const getFirstUnassignedSourceSceneId = (): string|null => {
    const unassignedSourceScene = [...sortedScenes.value].find((scene: Scene) => {
      if (!hasAssignedSource(scene.id)) {
        return scene
      }
      return null
    })
    return unassignedSourceScene?.id ?? null
  }

  const hasAssignedSource = (sceneId: string): boolean => {
    return currentCast.value?.hasSpecificSourceType(sceneId) ?? false
  }

  return {
    getCasterNameInScene,
    getLastNonPreviewProgramSceneId,
    getFirstUnassignedSourceSceneId,
    getSceneNameBySceneId,
    hasAssignedSource,
    setSceneInEdit,
    setSceneInPreview,
    sortedScenes
  }
}

export default useCastScenes
